import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SecurityRoleService } from '@app/modules/security-roles/services/security-role.service';
import { CustomReportsService } from '@app/modules/custom-reports/services/reports.service';

@Component({
  selector: 'app-assign-report-security-dialog',
  templateUrl: './assign-report-security-dialog.component.html',
  styleUrls: ['./assign-report-security-dialog.component.scss']
})
export class AssignReportSecurityDialogComponent implements OnInit {
  public reportId: string;
  public reportName: string;
  public gridDataResult: GridDataResult;
  public isLoading: boolean;
  public pageSize: number = 20;
  public skip: number = 0;
  public searchValue: string;
  public searchFilterString: string;
  private sortString = 'name-asc';
  public selectedItems: string[] = [];
  private initialRoleIds: string[] = [];
  private getRolesRequest: any;

  public columns: any[] = [
    { field: 'name', title: 'Role Name' },
  ];

  constructor(
    private dialogRef: MatDialogRef<AssignReportSecurityDialogComponent>,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private securityRoleService: SecurityRoleService,
    private customReportsService: CustomReportsService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.reportId = data.reportId;
    this.reportName = data.reportName;
  }

  ngOnInit(): void {
    this.loadAssignedRoles();
  }

  private loadAssignedRoles(): void {
    this.overlayService.show();

    this.customReportsService.getReportsSecurity(100, 0, null, `report.Id = "${this.reportId}"`)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: (pagedSecurity) => {
          this.initialRoleIds = (pagedSecurity?.data || []).map(security => security.role.id);
          this.selectedItems = [...this.initialRoleIds];
          this.getRoles();
        },
        error: () => {
          this.getRoles();
        }
      });
  }

  getRoles(): void {
    this.isLoading = true;

    if (this.getRolesRequest) {
      this.getRolesRequest.unsubscribe();
    }

    this.getRolesRequest = this.securityRoleService.getSecurityRoles(this.pageSize, this.skip, this.searchFilterString, this.sortString)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(
        pagedRoles => {
          this.gridDataResult = {
            data: pagedRoles.data,
            total: pagedRoles.total,
          };
        }
      );
  }

  search(): void {
    this.searchFilterString = this.searchValue ? `(Name like "${this.searchValue}")` : null;
    this.skip = 0;
    this.getRoles();
  }

  pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.getRoles();
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const roleIdsToAdd = this.selectedItems.filter(id => !this.initialRoleIds.includes(id));
    const roleIdsToRemove = this.initialRoleIds.filter(id => !this.selectedItems.includes(id));

    if (roleIdsToAdd.length === 0 && roleIdsToRemove.length === 0) {
      this.dialogRef.close();
      return;
    }

    const observables = [
      ...roleIdsToAdd.map(roleId => defer(() => this.customReportsService.addReportSecurity(this.reportId, roleId))),
      ...roleIdsToRemove.map(roleId => defer(() => this.customReportsService.removeReportSecurity(this.reportId, roleId))),
    ];

    this.overlayService.show();

    forkJoin(observables)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Report security updated successfully', 'clear', 'success');
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to update report security', 'clear', 'warn');
        }
      });
  }
}
